<template>
  <v-card dflex>
    <v-toolbar color="primary" flat dark prominent>
      <v-toolbar-title>{{ dealer.name }}</v-toolbar-title>
      <v-spacer/>
      <v-card-subtitle class="white--text">Email Response Report</v-card-subtitle>
      <v-btn
        color="primary"
        fab
        right
        x-small
        elevation="0"
        v-on:click.prevent="$emit('close-report')"
      >
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <template v-for="campaignStat in mailStatsData">
        <v-row no-gutters :key="campaignStat.campaign_id + 'title'">
          <v-toolbar color="primary" flat dark dense>
            <v-toolbar-title class="ml-2">{{ campaignStat.campaign_name }}</v-toolbar-title>
          </v-toolbar>
        </v-row>
        <v-row no-gutters class="justify-center" :key="campaignStat.campaign_id">
          <!-- hiding due to confusion around what cleaned means
          <circle-chart
            :value="totalAudienceChartData(campaignStat)"
          ></circle-chart>
          -->
          <circle-chart
            :value="totalSentChartData(campaignStat)"
          ></circle-chart>
          <circle-chart
            :value="totalDeliveredChartData(campaignStat)"
          ></circle-chart>
        </v-row>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { ReportsApi } from '@/services'
import { downloadViaLinkCreated, hasValue } from '@/utils'
import { formatAsPercent } from '@/components/filters/percent'
import CircleChart from '../components/CircleChart'
import { DateTime } from 'luxon'

const TOTAL_AUDIENCE_CHART_KEYS = ['received', 'dropped', 'cleaned', 'sent']
const TOTAL_SENT_CHART_KEYS = ['sent', 'delivered', 'bounce']
const TOTAL_DELIVERED_CHART_KEYS = ['delivered', 'unique_open', 'unique_click', 'unsubscribe', 'spam']

export default {
  name: 'email-response-report',
  components: {
    [CircleChart.name]: CircleChart
  },
  props: {
    // when set to true, indicate report should be executed now
    runReport: {
      required: true,
      type: Boolean
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    },
    emailCampaigns: {
      type: Array
    },
    outputFormat: {
      type: String,
      required: false,
      default: 'html'
    }
  },
  data: function () {
    return {
      salesColor: 'teal',
      servicesColor: 'cyan',
      mailStatsData: [],
      dollarTextShrink: {
        default: {
          // width: 200,
          valueClass: 'headline ml-2 mb-8'
        }
      }
    }
  },
  computed: {
    ...mapState('DealerStore', ['dealer']),
    fStartDate () {
      return this.dateVerboseMDY(this.startDate)
    },
    fEndDate () {
      return this.dateVerboseMDY(this.endDate)
    }
  },
  watch: {
    runReport (newVal, oldVal) {
      if (newVal) {
        this.loadData()
      }
    }
  },
  methods: {
    totalAudienceChartData (campaignStats) {
      const statData = Object.keys(campaignStats).filter(label => TOTAL_AUDIENCE_CHART_KEYS.includes(label.replace('total_', '')))
        .reduce((accum, statLabel) => {
          accum[statLabel.replace('total_', '')] = campaignStats[statLabel] || 0
          return accum
        }, {})

      const lbls = ['Valid', 'Cleaned', 'Dropped']
      const lblPercentages = [
        { label: 'Valid', value: formatAsPercent(campaignStats.valid_pct, 2, false) },
        { label: 'Cleaned', value: formatAsPercent(campaignStats.cleaned_pct, 2, false) },
        { label: 'Dropped', value: formatAsPercent(campaignStats.dropped_pct, 2, false) }
      ]
      const ctData = [statData.sent || 0, statData.cleaned || 0, statData.dropped || 0]

      return {
        legendTitle: 'Total Audience',
        legendCount: statData.received || 0,
        chartLabels: lbls,
        labelPercentages: lblPercentages,
        chartData: ctData,
        chartHeading: formatAsPercent(campaignStats.valid_pct, 2, false),
        chartTitle: statData.sent || 0,
        chartSubtitle: 'Valid'
      }
    },
    totalSentChartData (campaignStats) {
      const statData = Object.keys(campaignStats).filter(label => TOTAL_SENT_CHART_KEYS.includes(label.replace('total_', '')))
        .reduce((accum, statLabel) => {
          accum[statLabel.replace('total_', '')] = campaignStats[statLabel] || 0
          return accum
        }, {})

      const lbls = ['Delivered', 'Bounced']
      const lblPercentages = [
        { label: 'Delivered', value: formatAsPercent(campaignStats.delivered_pct, 2, false) },
        { label: 'Bounced', value: formatAsPercent(campaignStats.bounce_pct, 2, false) }
      ]
      const ctData = [statData.delivered || 0, statData.bounce || 0]

      return {
        legendTitle: 'Total Sent',
        legendCount: statData.sent || 0,
        chartLabels: lbls,
        labelPercentages: lblPercentages,
        chartData: ctData,
        chartHeading: formatAsPercent(campaignStats.delivered_pct, 2, false),
        chartTitle: statData.delivered || 0,
        chartSubtitle: 'Delivered'
      }
    },
    totalDeliveredChartData (campaignStats) {
      const statData = Object.keys(campaignStats).filter(label => TOTAL_DELIVERED_CHART_KEYS.includes(label.replace('total_', '')))
        .reduce((accum, statLabel) => {
          accum[statLabel.replace('total_', '')] = campaignStats[statLabel] || 0
          return accum
        }, {})

      const lbls = ['Unique Opens', 'Unique Clicks', 'Unsubscribes', 'Spam', 'noop']
      const lblPercentages = [
        { label: 'Unique Opens', value: parseFloat(formatAsPercent(campaignStats.unique_open_pct, 2, false)) },
        { label: 'Unique Clicks', value: parseFloat(formatAsPercent(campaignStats.unique_click_pct, 2, false)) },
        { label: 'Unsubscribes', value: parseFloat(formatAsPercent(campaignStats.unsubscribe_pct, 2, false)) },
        { label: 'Spam', value: parseFloat(formatAsPercent(campaignStats.spam_pct, 2, false)) }
      ]
      const actionPct = lblPercentages.map(lpct => lpct.value).reduce((a, b) => { return a + b }, 0)
      const noActionPct = (100.00 - actionPct).toFixed(2)
      lblPercentages.push({ label: 'noop', value: noActionPct })

      // const unsubCount = statData['unsubscribe-global'] || 0 + statData['unsubscribe-campaign'] || 0
      // 4 events - missing no action - have to calculate it
      const ctData = [statData.unique_open || 0, statData.unique_click || 0, statData.unsubscribe || 0, statData.spam || 0]

      // evtBreakdown is the sme as ctData but does not include any open counts... that would count again
      // const evtBreakdown = [statData.open || 0, statData.click || 0, statData.unsubscribe || -1, statData.spam || 0]
      const evtBreakdown = [statData.unique_open || 0, statData.unique_click || 0, statData.unsubscribe || -1, statData.spam || 0]
      const actionEvtCt = evtBreakdown.reduce((a, b) => { return a + b }, 0) || 0
      ctData.push((statData.delivered - actionEvtCt) || 0)

      return {
        legendTitle: 'Total Delivered',
        legendCount: statData.delivered || 0,
        chartLabels: lbls,
        labelPercentages: lblPercentages,
        chartData: ctData,
        chartHeading: formatAsPercent(campaignStats.unique_open_pct, 2, false),
        chartTitle: statData.unique_open || 0,
        chartSubtitle: 'Opens'
      }
    },
    shouldDownload () {
      return ['excel', 'csv'].includes(this.outputFormat)
    },
    dateVerboseMDY (date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED)
    },
    validate () {
      if (hasValue(this.startDate) && hasValue(this.endDate) && this.emailCampaigns.length > 0) {
        return true
      } else {
        throw new Error('Invalid/Missing Parameters')
      }
    },
    async loadData () {
      this.$logger.trace('Loading EmailResponseReport Data')
      try {
        this.validate()
        const rptParams = {
          name: 'EmailResponseReport',
          dealer_id: this.dealer.id,
          start_date: this.startDate,
          end_date: this.endDate,
          email_campaigns: this.emailCampaigns,
          include_backing_data: this.shouldDownload(),
          output_format: this.outputFormat
        }
        const rptData = await ReportsApi.generateReport(rptParams)
        if (this.shouldDownload()) {
          downloadViaLinkCreated(rptData.data.csv_url)
        } else {
          this.mailStatsData = rptData.data
        }

        this.$emit('report-complete', { status: 'ok' })
      } catch (error) {
        this.$logger.error('Error Fetching report', error.message)
        this.$emit('report-complete', { status: 'error', message: error.message })
      }
    }
  }

}
</script>
